import { useEffect, useRef, useState } from "react";
import type { ClubView, MarketFilters, MarketPlayerRow, OfferRow, OrdemDoMercado } from "../game/types";
import { Crest, Panel, PositionChip, StrengthBadge } from "../ui/atoms";
import {
  CabecalhoDaTabela,
  CelulasDaLinha,
  ControlesDaTabela,
  useTabela,
  type Coluna,
} from "../ui/tabela";
import { dinheiro, rotuloDeCaracteristica, rotuloDePosicao, useT } from "../i18n";

const POSITIONS = ["GOL", "ZAG", "LD", "LE", "VOL", "MC", "MEI", "PD", "PE", "ATA"];

const EMPTY: MarketFilters = {
  text: "",
  position: null,
  maxAge: null,
  minStrength: null,
  maxValue: null,
  freeAgentsOnly: false,
  affordableOnly: true,
};

/**
 * Mercado de transferências.
 *
 * A busca roda no worker, sobre os mais de oito mil jogadores do mundo, e volta
 * com os sessenta melhores do filtro. A proposta não fecha nada na hora: entra
 * na fila e é resolvida na virada da rodada, junto com as dos outros clubes.
 * Quando dois clubes disputam o mesmo jogador, a maior oferta é ouvida primeiro,
 * que é o leilão herdado do Elifoot.
 */
/**
 * As colunas do mercado.
 *
 * Seis não cabem num celular, então quatro delas são de destaque e a pessoa
 * escolhe uma por vez. Posição e jogador ficam sempre: são o que diz de quem
 * é a linha.
 *
 * O preço e o que o jogador pede de salário são as duas perguntas que se faz
 * aqui, e por isso as duas ordenam. Preço começa do mais barato, ao contrário
 * do resto: ninguém procura o jogador mais caro que existe.
 */
function useColunasDoMercado(): Array<Coluna<MarketPlayerRow>> {
  const t = useT();

  return [
    {
      chave: "pos",
      rotulo: t("mercado.colunas.pos"),
      presenca: "sempre",
      largura: 48,
      celula: (player) => <PositionChip pos={player.pos} />,
    },
    {
      chave: "jogador",
      rotulo: t("mercado.colunas.jogador"),
      presenca: "sempre",
      ordenar: (player) => player.name,
      direcaoInicial: "asc",
      celula: (player) => (
        <div style={{ minWidth: 0 }}>
          <div className="corta" style={{ fontWeight: 600 }}>
            {player.name}
            {player.transferListed && (
              <span style={{ color: "var(--warning)", fontSize: 10, marginLeft: 6 }}>
                {t("mercado.naLista")}
              </span>
            )}
          </div>
          <div className="muted corta" style={{ fontSize: 10.5 }}>
            {t("mercado.linhaSecundaria", {
              idade: player.age,
              destaques: player.highlights
                .map((key) => rotuloDeCaracteristica(key))
                .join(" · "),
            })}
          </div>
        </div>
      ),
    },
    {
      chave: "clube",
      rotulo: t("mercado.colunas.clube"),
      presenca: "destaque",
      ordenar: (player) => (player.clubId ? player.clubName : ""),
      direcaoInicial: "asc",
      celula: (player) => (
        <span className="muted corta" style={{ fontSize: 11.5, display: "block" }}>
          {player.clubId ? player.clubName : t("comum.semClube")}
        </span>
      ),
    },
    {
      chave: "forca",
      rotulo: t("mercado.colunas.forca"),
      presenca: "destaque",
      largura: 40,
      direita: true,
      ordenar: (player) => player.strength,
      celula: (player) => <StrengthBadge value={player.strength} />,
    },
    {
      chave: "preco",
      rotulo: t("mercado.colunas.preco"),
      presenca: "destaque",
      largura: 90,
      direita: true,
      // Jogador livre custa zero, e é isso que o põe no começo da lista mais
      // barata, que é onde ele de fato deveria aparecer.
      ordenar: (player) => (player.clubId ? player.askingPrice : 0),
      direcaoInicial: "asc",
      celula: (player) => (
        <span
          className="num"
          style={{
            whiteSpace: "nowrap",
            color: player.affordable ? "inherit" : "var(--danger)",
          }}
        >
          {player.clubId ? dinheiro(player.askingPrice) : t("mercado.livre")}
        </span>
      ),
    },
    {
      chave: "pede",
      rotulo: t("mercado.colunas.pede"),
      presenca: "destaque",
      largura: 90,
      direita: true,
      // Quem não vem de jeito nenhum vai para o fim das duas ordens.
      ordenar: (player) => player.askingSalary ?? Number.MAX_SAFE_INTEGER,
      direcaoInicial: "asc",
      celula: (player) => (
        <span className="num" style={{ whiteSpace: "nowrap" }}>
          {player.askingSalary === null ? t("mercado.naoVem") : dinheiro(player.askingSalary)}
        </span>
      ),
    },
  ];
}

export function MarketPanel({
  view,
  results,
  total,
  pagina,
  paginas,
  busy,
  onSearch,
  onOpenPlayer,
  selecionado,
  onRespond,
}: {
  view: ClubView;
  results: MarketPlayerRow[];
  total: number;
  /** A página que o motor devolveu, e quantas existem para este filtro. */
  pagina: number;
  paginas: number;
  busy: boolean;
  onSearch: (filters: MarketFilters, pagina: number, ordem: OrdemDoMercado | null) => void;
  onOpenPlayer: (playerId: string) => void;
  /** Jogador aberto na ficha ao lado, quando ela existe. */
  selecionado?: string | undefined;
  onRespond: (offerId: string, action: "aceitar" | "recusar") => void;
}) {
  const t = useT();
  const [filters, setFilters] = useState<MarketFilters>(EMPTY);
  // Sem `procurar`: a busca por nome desta tela é a de cima, que vai ao motor
  // com os outros filtros. A daqui só ordenaria o que já veio.
  const tabela = useTabela<MarketPlayerRow>({
    colunas: useColunasDoMercado(),
    destaqueInicial: "preco",
  });

  // Quem acabou de ser comprado sai da lista na hora. A busca é de antes da
  // compra, e ele continuaria ali, com o clube antigo, até a próxima busca.
  const meus = new Set(view.squad.map((jogador) => jogador.id));
  const disponiveis = results.filter((jogador) => !meus.has(jogador.id));

  // A ordem da tabela vai ao motor, que ordena a lista inteira e devolve a
  // página. Trocar de coluna (e abrir a aba) volta para a primeira página.
  const ordem = tabela.ordem;
  const chaveDaOrdem = ordem ? `${ordem.chave}:${ordem.direcao}` : "";
  useEffect(() => {
    onSearch(filters, 1, ordem);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveDaOrdem]);

  function update(patch: Partial<MarketFilters>): void {
    const next = { ...filters, ...patch };
    setFilters(next);
    onSearch(next, 1, ordem);
  }

  // Página nova começa do topo da lista, e não de onde a anterior parou.
  const lista = useRef<HTMLDivElement>(null);
  function irPara(destino: number): void {
    onSearch(filters, destino, ordem);
    lista.current?.scrollTo({ top: 0 });
  }

  const reabertura =
    view.marketOpensRound !== null
      ? t("mercado.reabreNaRodada", { rodada: view.marketOpensRound })
      : t("mercado.reabreNaPreTemporada");

  return (
    <div className="grid mercado" style={{ gap: 14 }}>
      {!view.marketOpen && (
        <div
          className="card"
          style={{
            padding: "10px 14px",
            borderColor: "var(--warning)",
            color: "var(--warning)",
            fontWeight: 600,
          }}
        >
          {`${t("mercado.janelaFechada")} ${reabertura}`}
        </div>
      )}

      {view.offersIn.length > 0 && (
        <Panel title={t("mercado.propostasRecebidas", { total: view.offersIn.length })}>
          <div className="grid" style={{ gap: 10 }}>
            {view.offersIn.map((offer) => (
              <OfferCard
                key={offer.id}
                offer={offer}
                view={view}
                busy={busy}
                onRespond={onRespond}
                incoming
              />
            ))}
          </div>
        </Panel>
      )}

      {view.offersOut.length > 0 && (
        <Panel title={t("mercado.propostasEnviadas", { total: view.offersOut.length })}>
          <div className="grid" style={{ gap: 10 }}>
            {view.offersOut.map((offer) => (
              <OfferCard key={offer.id} offer={offer} view={view} busy={busy} incoming={false} />
            ))}
          </div>
        </Panel>
      )}

      <Panel
        className="mercado-busca"
        title={t("mercado.procurarJogador")}
        action={<span className="label-muted">{t("mercado.encontrados", { total })}</span>}
      >
        <div className="row" style={{ gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          {/* O atributo é o alvo do atalho "/", em MARCA_DE_BUSCA de src/ui/atalhos.ts. */}
          <input
            data-busca-de-jogador=""
            placeholder={t("mercado.nomeDoJogador")}
            value={filters.text}
            onChange={(e) => update({ text: e.target.value })}
            style={{ minWidth: 180 }}
          />
          <select
            value={filters.position ?? ""}
            onChange={(e) => update({ position: e.target.value || null })}
          >
            <option value="">{t("mercado.todasAsPosicoes")}</option>
            {/* O valor continua sendo a sigla do motor: só o rótulo é traduzido. */}
            {POSITIONS.map((pos) => (
              <option key={pos} value={pos}>
                {rotuloDePosicao(pos)}
              </option>
            ))}
          </select>
          <select
            value={filters.maxAge ?? ""}
            onChange={(e) => update({ maxAge: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">{t("mercado.qualquerIdade")}</option>
            {[20, 23, 26, 29, 32].map((age) => (
              <option key={age} value={age}>
                {t("mercado.ateAnos", { idade: age })}
              </option>
            ))}
          </select>
          <select
            value={filters.minStrength ?? ""}
            onChange={(e) =>
              update({ minStrength: e.target.value ? Number(e.target.value) : null })
            }
          >
            <option value="">{t("mercado.qualquerForca")}</option>
            {[50, 60, 70, 80, 85].map((value) => (
              <option key={value} value={value}>
                {t("mercado.forcaOuMais", { valor: value })}
              </option>
            ))}
          </select>
          <label className="row" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={filters.freeAgentsOnly}
              onChange={(e) => update({ freeAgentsOnly: e.target.checked })}
              style={{ minHeight: 0, width: 16, height: 16 }}
            />
            <span className="muted">{t("mercado.soLivres")}</span>
          </label>
          <label className="row" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={filters.affordableOnly}
              onChange={(e) => update({ affordableOnly: e.target.checked })}
              style={{ minHeight: 0, width: 16, height: 16 }}
            />
            <span className="muted">{t("mercado.soOQueCabe")}</span>
          </label>
        </div>

        <ControlesDaTabela tabela={tabela} />

        <div className="scroll" style={{ maxHeight: "48vh" }} ref={lista}>
          <table className="fixa">
            <CabecalhoDaTabela tabela={tabela} />
            <tbody>
              {disponiveis.map((player) => (
                <tr
                  key={player.id}
                  className="player"
                  aria-selected={selecionado === undefined ? undefined : player.id === selecionado}
                  onClick={() => onOpenPlayer(player.id)}
                  style={{ cursor: "pointer", opacity: player.askingSalary === null ? 0.45 : 1 }}
                >
                  <CelulasDaLinha tabela={tabela} linha={player} />
                </tr>
              ))}
              {disponiveis.length === 0 && (
                <tr>
                  <td
                    colSpan={tabela.visiveis.length}
                    className="muted"
                    style={{ padding: 20, textAlign: "center" }}
                  >
                    {t("mercado.nenhumJogador")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {paginas > 1 && (
          <nav className="row mercado-paginas" aria-label={t("mercado.paginas")}>
            <button type="button" className="ghost" disabled={busy || pagina <= 1} onClick={() => irPara(1)} aria-label={t("mercado.primeiraPagina")}>
              «
            </button>
            <button type="button" className="ghost" disabled={busy || pagina <= 1} onClick={() => irPara(pagina - 1)} aria-label={t("mercado.paginaAnterior")}>
              ‹
            </button>
            <span className="num mercado-pagina-atual">{t("mercado.paginaDe", { pagina, paginas })}</span>
            <button type="button" className="ghost" disabled={busy || pagina >= paginas} onClick={() => irPara(pagina + 1)} aria-label={t("mercado.proximaPagina")}>
              ›
            </button>
            <button type="button" className="ghost" disabled={busy || pagina >= paginas} onClick={() => irPara(paginas)} aria-label={t("mercado.ultimaPagina")}>
              »
            </button>
          </nav>
        )}
      </Panel>
    </div>
  );
}

function OfferCard({
  offer,
  view,
  busy,
  incoming,
  onRespond,
}: {
  offer: OfferRow;
  view: ClubView;
  busy: boolean;
  incoming: boolean;
  onRespond?: (offerId: string, action: "aceitar" | "recusar") => void;
}) {
  const t = useT();
  // Pelo identificador do jogador, e não pelo nome dentro da frase: a frase
  // deixou de ser texto pronto e virou chave, e procurar nome em texto sempre
  // foi um jeito de acertar por acaso.
  // A resposta desta proposta, se já foi dada. Pela proposta e não pelo
  // jogador: com duas propostas pelo mesmo jogador, a outra continua aberta
  // para quem quiser trocar a escolha.
  const resposta = view.pending.find((entry) => entry.offerId === offer.id);
  const roundsLeft = offer.expiresRound - view.round;

  const cabecalho = incoming
    ? t("mercado.ofertaRecebida", {
        clube: offer.otherClubName || t("comum.semClube"),
        valor: dinheiro(offer.amount),
      })
    : t("mercado.ofertaEnviada", {
        clube: offer.otherClubName || t("comum.semClube"),
        valor: dinheiro(offer.amount),
      });
  const prazo =
    incoming && roundsLeft > 0 ? ` · ${t("mercado.respondeEm", { rodadas: roundsLeft })}` : "";

  return (
    <div
      className="row"
      style={{
        gap: 12,
        padding: "10px 12px",
        background: "var(--surface-2)",
        borderRadius: 8,
        /*
         * Os botões descem para a linha de baixo quando a largura aperta.
         *
         * Num celular de 360 pontos, "Recusar" e "Aceitar" comem metade da
         * linha e sobra uma coluna de texto tão estreita que a proposta inteira
         * vira sete linhas de uma palavra cada. A base de 200 pontos para o
         * texto é o que decide: enquanto couber, tudo fica numa linha só como
         * no computador; quando não couber, a resposta vai para baixo e o texto
         * recupera a largura.
         */
        flexWrap: "wrap",
      }}
    >
      <Crest colors={offer.otherClubColors} short={offer.otherClubShort} size={34} />
      <div style={{ flex: "1 1 200px", minWidth: 0 }}>
        <div style={{ fontWeight: 600 }}>
          {offer.playerName}{" "}
          <span className="muted" style={{ fontWeight: 400, fontSize: 11.5 }}>
            {t("mercado.jogadorResumo", {
              pos: rotuloDePosicao(offer.playerPos),
              forca: offer.playerStrength,
            })}
          </span>
        </div>
        <div className="muted" style={{ fontSize: 11.5 }}>
          {`${cabecalho}${prazo}`}
        </div>
      </div>

      {incoming && onRespond && (
        <div className="row" style={{ gap: 6, marginLeft: "auto" }}>
          {resposta ? (
            <span
              className="label"
              style={{
                whiteSpace: "nowrap",
                color: resposta.offerAction === "aceitar" ? "var(--success)" : "var(--danger)",
              }}
            >
              {resposta.offerAction === "aceitar" ? t("mercado.aceitaNaFila") : t("mercado.recusadaNaFila")}
            </span>
          ) : (
            <>
              <button className="ghost" disabled={busy} onClick={() => onRespond(offer.id, "recusar")}>
                {t("mercado.recusar")}
              </button>
              <button className="primary" disabled={busy} onClick={() => onRespond(offer.id, "aceitar")}>
                {t("mercado.aceitar")}
              </button>
            </>
          )}
        </div>
      )}
      {!incoming && <span className="label-muted">{t("mercado.aguardando")}</span>}
    </div>
  );
}
